import React from 'react';

import useIsAcademyUser from 'app/hooks/useIsAcademyUser';
import type { AcademyMode } from 'app/helpers/academyMode';
import { GraduationCapOutlineIcon, OfficeIcon } from '../../../../icons';
import { DRAWER_ICON_PROPS } from '../../MenuConfig';
import { trackAcademyModeSwitched } from '../../trackMenuEvents';
import MenuDrawerItem from '../MenuDrawerItem';
import * as S from './styles';

/* Frase do tooltip (e do item no drawer mobile), pelo modo em que o usuário ESTÁ. */
export const ACADEMY_MODE_SWITCH_TOOLTIP: Record<AcademyMode, string> = {
  academy: 'Você está na Academy · ir para a Jusfy',
  advogado: 'Você está na Jusfy · ir para a Academy',
};

export const ACADEMY_MODE_LABEL: Record<AcademyMode, string> = {
  academy: 'Academy',
  advogado: 'Jusfy',
};

/* O track do Segment é fire-and-forget (o wrapper não expõe callback) e a troca
   recarrega a página: recarregar na mesma volta descartaria o evento. Uma
   folga curta deixa o analytics.js despachar antes. */
export const ACADEMY_MODE_SWITCH_TRACK_GRACE_MS = 300;

type AcademyModeSwitchProps = {
  variant?: 'desktop' | 'mobile';
  /** Chamado antes de trocar (o drawer mobile fecha aqui). */
  onBeforeSwitch?: () => void;
};

const iconFor = (mode: AcademyMode) =>
  React.createElement(
    mode === 'academy' ? GraduationCapOutlineIcon : OfficeIcon,
    DRAWER_ICON_PROPS,
  );

/**
 * Chave Academy ⇄ Jusfy: capelo de um lado, balança do outro. O lado aceso é
 * onde o usuário está; clicar no outro troca de app. Só existe para quem é
 * dual (aluno com a flag EnableAcademySwitch, ver useIsAcademyUser); para o
 * resto não renderiza nada. No sidebar fica logo abaixo do logo, separada do
 * menu; no drawer mobile vira a primeira linha, com a frase inteira.
 */
const AcademyModeSwitch: React.FC<AcademyModeSwitchProps> = ({
  variant = 'desktop',
  onBeforeSwitch,
}) => {
  const { isDualUser, academyMode, setAcademyMode } = useIsAcademyUser();

  if (!isDualUser) return null;

  const target: AcademyMode = academyMode === 'academy' ? 'advogado' : 'academy';
  const tooltip = ACADEMY_MODE_SWITCH_TOOLTIP[academyMode];

  const switchTo = (mode: AcademyMode) => {
    if (mode === academyMode) return;
    trackAcademyModeSwitched(mode);
    onBeforeSwitch?.();
    window.setTimeout(
      () => setAcademyMode(mode),
      ACADEMY_MODE_SWITCH_TRACK_GRACE_MS,
    );
  };

  if (variant === 'mobile') {
    return (
      <MenuDrawerItem
        icon={iconFor(target)}
        label={tooltip}
        isCollapsed={false}
        onClick={() => switchTo(target)}
      />
    );
  }

  return (
    <S.Wrapper>
      <S.Toggle
        role="group"
        aria-label="Trocar entre Academy e Jusfy"
        data-tooltip-id="sidebar-icon-tooltip"
        data-tooltip-content={tooltip}
      >
        {(['academy', 'advogado'] as const).map((mode) => (
          <S.Cell
            key={mode}
            type="button"
            $on={mode === academyMode}
            aria-pressed={mode === academyMode}
            aria-label={ACADEMY_MODE_LABEL[mode]}
            onClick={() => switchTo(mode)}
          >
            {iconFor(mode)}
          </S.Cell>
        ))}
      </S.Toggle>
      <S.Divider aria-hidden />
    </S.Wrapper>
  );
};

export default AcademyModeSwitch;
